import React from 'react'
import Input from '../Input'
import Button from '../Button'
import Error from './Error'
import { gsColors } from '../../styles/Variables'
import SignInModal from './SignInModal'
import { Text } from '../../styles/Components'
import { useAuthContext } from './reducer'
import PhoneNumberInput from '../../components/phone-number-input'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { validateEmail } from '../../helpers/final-form-input-components'
import { OTPInput } from './otp-input'
import { SocialButtons } from './social-buttons'
import { AgreementLink } from './styles'

/*
 * SignInForm — the OTP-only entry point.
 *
 * Renders one of two screens depending on state:
 *   1. The OTP entry screen (once a code has been sent), or
 *   2. A single identifier-entry screen (phone OR email) driven by `state.otpChannel`.
 *
 * There is no email/password login and no "Forgot Password?" link anymore; the
 * password-reset modal only surfaces via the bpPortal deep-link (`passwordResetModal`).
 */
export const SignInForm = ({
  handleInput,
  handleOnFocus,
  handleOnBlur,
  handleOTPExchange,
  handlePasswordlessStart,
  handleResend,
  switchChannel,
  handleProviderAuth,
  isDesktop,
  passwordResetModal,
  closeSignInModal
}) => {
  const { state } = useAuthContext()
  const { otpChannel, showOTPInput, working } = state
  const isEmail = otpChannel === 'email'

  const handlePhoneChange = (value) => {
    handleInput({
      target: {
        name: 'phone',
        value: value || ''
      }
    })
  }

  // OTP entry screen — shared by both channels
  if (showOTPInput) {
    return (
      <OTPInput
        submitError={state.submitError}
        handleSubmit={handleOTPExchange}
        handleResend={handleResend}
      />
    )
  }

  // Continue is disabled until the current channel's identifier is valid
  const sendDisabled = isEmail
    ? !!validateEmail(state.email.value)
    : !isValidPhoneNumber(state.phone.value)

  // Submitting the form sends the code. This is a real <form> submit rather than a click
  // handler so the mobile keyboard offers "Go" and Enter works — the natural next action
  // after the browser or password manager autofills the identifier.
  const handleSubmit = (event) => {
    event.preventDefault()
    if (sendDisabled || working) {
      return
    }
    handlePasswordlessStart()
  }

  return (
    <>
      {/*
        A named <form> (rather than a bare div) is what browser autofill, iCloud Keychain and
        password managers key off to recognize this as a sign-in and offer stored contacts.
        The password-reset modal renders as a sibling below — forms cannot be nested.
      */}
      <form
        className='otp-login'
        id='sign-in-form'
        name='sign-in-form'
        autoComplete='on'
        noValidate
        onSubmit={handleSubmit}
      >
        {isEmail ? (
          <div className='field-container email-container'>
            <Input
              className={`email-address ${state.email.error ? 'input-error' : ''}`}
              placeholder='Email Address'
              type='email'
              name='email'
              value={state.email.value}
              onChange={handleInput}
              onFocus={handleOnFocus}
              onBlur={handleOnBlur}
              autoComplete='email'
              inputMode='email'
              enterKeyHint='go'
              autoCapitalize='none'
              autoCorrect='off'
              spellCheck={false}
            />
            {state.email.error && <p className='error-message'>Please enter a valid email</p>}
          </div>
        ) : (
          <div className={`field-container phone-input-container ${state.phone.error ? 'phone-error' : ''}`}>
            {/* react-phone-number-input sets type="tel" itself and forwards these to the input */}
            <PhoneNumberInput
              value={state.phone.value}
              onChange={handlePhoneChange}
              onFocus={handleOnFocus}
              onBlur={handleOnBlur}
              autoComplete='tel'
              name='phone'
              enterKeyHint='go'
            />
          </div>
        )}

        <Error error={state.submitError} />

        <Button
          fill='true'
          type='submit'
          large
          className='continue-button'
          color={gsColors.cobalt}
          disabled={sendDisabled}
          working={working}
        >
          Continue
        </Button>

        <div className='divider-with-text'>
          <span>or continue with</span>
        </div>

        {/* every button in this row is type="button", so none of them submit the form */}
        <SocialButtons switchChannel={switchChannel} onProviderCredential={handleProviderAuth} />

        <Text className='text-agreement' color={gsColors.mediumGrey}>
          We&apos;ll send a confirmation code by text or email. Message and data rates apply.{' '}
          <AgreementLink href='/privacypolicy' target='_blank' rel='noopener noreferrer'>Privacy Policy</AgreementLink>
        </Text>
      </form>

      {/* Reset modal is only reachable via the bpPortal deep-link; no visible link opens it */}
      <SignInModal
        isOpen={passwordResetModal}
        close={closeSignInModal}
        isReset={true}
        isDesktop={isDesktop}
      />
    </>
  )
}
